"use client";

import { useLocale } from "next-intl";
import { ComponentProps } from "react";
import blogDisclaimerDE from "../../../../../messages/public/legal-informations/blogDisclaimer/de.json";
import blogDisclaimerEN from "../../../../../messages/public/legal-informations/blogDisclaimer/en.json";
import TitleH2 from "../fonts/TitleH2";
import BlogImage from "./BlogImage";

type ContentProps = ComponentProps<typeof BlogImage> & {
  title: string;
  text: string | React.ReactNode;
  imageLeft?: boolean;
  imageTop?: boolean;
  imageFit?: "cover" | "contain";
  objectPosition?: string;
  hideTitle?: boolean;
  colImagePortraitFormat?: boolean;
  noShadow?: boolean;
  disclaimer?: "default" | "legal";
};

const Content = ({ ...props }: ContentProps) => {
  const locale = useLocale();
  const { blogDisclaimer } =
    locale === "en" ? blogDisclaimerEN : blogDisclaimerDE;

  const disclaimerNode = props.disclaimer && (
    <div className="gs--blog-section__disclaimer mt-20 text-center text-sm text-neutral-500 italic">
      <p>{blogDisclaimer.default}</p>
      {props.disclaimer === "legal" && <p>{blogDisclaimer.legalAddendum}</p>}
    </div>
  );

  /* ── imageTop variant: centered stacked layout (unchanged) ── */
  if (props.imageTop) {
    return (
      <section className="gs--blog-section gs--blog-section--image-top gs--page-column mb-16 flex flex-col items-center text-center">
        {!props.hideTitle && (
          <TitleH2
            title={props.title}
            className="gs--blog-section__title text-primary pb-5"
          />
        )}
        <BlogImage
          wrapperClassName="mb-4"
          className="gs--blog-section__image"
          src={props.src}
          alt={props.alt}
          width={props.width}
          height={props.height}
        />
        <div className="gs--blog-section__body text-ring">{props.text}</div>
        {disclaimerNode}
      </section>
    );
  }

  /* ── Two-column alternating layout ── */
  const imageRight = !props.imageLeft;
  const isCover = props.imageFit === "cover";

  return (
    <section className="gs--blog-section gs--page-column mb-16">
      <div
        className={`gs--blog-section__grid ${imageRight ? "gs--blog-section__grid--image-right" : ""} ${isCover ? "gs--blog-section__grid--cover" : ""}`}
      >
        {/* 
          Image column — always first in DOM (shows on top on mobile) 
          colImagePortraitFormat: resizing image in correct scale without cutting off the lower part at the bottom of the image. 
        */}
        <div className={`gs--blog-section__col-image${props.colImagePortraitFormat ? " gs--blog-section__col-image--portrait" : ""}`}>
          <BlogImage
            className={`gs--blog-section__image${props.noShadow ? " gs--blog-section__image--no-shadow" : ""}`}
            src={props.src}
            alt={props.alt}
            width={props.width}
            height={props.height}
            objectFit={isCover ? "cover" : undefined}
            objectPosition={
              isCover ? (props.objectPosition ?? "center") : undefined
            }
          />
        </div>

        {/* Text column — title + body */}
        <div className="gs--blog-section__col-text">
          {!props.hideTitle && (
            <TitleH2
              title={props.title}
              className="gs--blog-section__title text-primary pb-5"
            />
          )}
          <div className="gs--blog-section__body text-ring">{props.text}</div>
        </div>
      </div>
      {disclaimerNode}
    </section>
  );
};

export default Content;
